// src/services/authApi.ts
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

export type Role = 
  | 'System Admin' 
  | 'Exec Admin'
  | 'Airport Manager' 
  | 'Fraport User' 
  | 'Tenant User (365 Tenant Sense)' 
  | 'Client User';

export interface User {
  userId: number | string; // Backend returns integer from Users.userId (with GUID fallback if user not in Users table)
  email: string;
  displayName: string;
  roles: Role[]; // Backend returns roles as an array
  isActive: boolean;
  lastLoginAt: string | null;
  userType?: string | null; // 'FRAPORT' | 'CLIENT' | 'TENANT' from Users.UserType
}

// Helper function to check if userId is a valid integer (for APIs that require integer userIds)
// Backend should return integer userId, but we check as a safety measure for GUID fallback cases
export function isIntegerUserId(userId: number | string): userId is number {
  return typeof userId === 'number' && Number.isInteger(userId);
}

// Helper function to get primary role (first role in array) for backward compatibility
export function getPrimaryRole(user: User | null): Role | null {
  return user?.roles && user.roles.length > 0 ? user.roles[0] : null;
}

// Helper function to check if user has a specific role
export function hasRole(user: User | null, role: Role): boolean {
  return user?.roles?.includes(role) ?? false;
}

// Helper function to check if user has any of the specified roles
export function hasAnyRole(user: User | null, roles: Role[]): boolean {
  if (!user?.roles || user.roles.length === 0) return false;
  return roles.some(role => user.roles.includes(role));
}

// Helper function to check if user has read-only admin access
export function hasReadOnlyAdminAccess(user: User | null): boolean {
  return hasRole(user, 'Exec Admin');
}

// Helper function to check if user has full admin access
export function hasFullAdminAccess(user: User | null): boolean {
  return hasRole(user, 'System Admin');
}

// Helper function to check if user can see all projects
export function canViewAllProjects(user: User | null): boolean {
  return hasFullAdminAccess(user) || hasReadOnlyAdminAccess(user);
}

// Helper function to check if user is Fraport type (can manipulate projects: create, Excel, Change Order)
export function isFraportUser(user: User | null): boolean {
  return user?.userType?.toUpperCase() === 'FRAPORT';
}

/**
 * Tenant or Client — public execution view and action-form dismiss behavior.
 * Prefer Users.UserType; if null (common when Identity role exists but DB UserType unset),
 * infer from roles (matches backend tasks.js / projects.js effectiveUserType).
 */
export function isTenantOrClientUser(user: User | null): boolean {
  if (!user) return false;
  const t = user.userType?.toUpperCase() ?? null;
  if (t === 'TENANT' || t === 'CLIENT') return true;
  if (t === 'FRAPORT') return false;
  return (
    hasRole(user, 'Tenant User (365 Tenant Sense)') ||
    hasRole(user, 'Client User')
  );
}

export interface AuthError {
  error: boolean;
  message: string;
  code: 'AUTH_REQUIRED' | 'INSUFFICIENT_PERMISSIONS' | 'NO_ROLE_ASSIGNED' | 'INVALID_CREDENTIALS' | 'ACCOUNT_LOCKED' | 'VALIDATION_ERROR' | 'TERMS_NOT_ACCEPTED';
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  token?: string; // JWT token from backend
  message?: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  confirmPassword: string;
  invitationToken: string;
  displayName?: string;
  termsAccepted: boolean; // Required - user must accept terms to register
}

export interface RegisterResponse {
  user: User;
  message: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  message: string;
}

export interface ResetPasswordRequest {
  email: string;
  token: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ResetPasswordResponse {
  message: string;
}

import { createAuthHeaders } from '../utils/apiHelpers';

// Helper function to create a fetch with timeout
function fetchWithTimeout(url: string, options: RequestInit, timeoutMs: number = 10000): Promise<Response> {
  return Promise.race([
    fetch(url, options),
    new Promise<Response>((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
    ),
  ]);
}

// Helper function to retry a fetch operation with exponential backoff
async function retryFetch<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      lastError = error;
      
      // Don't retry on authentication errors (401, 423) or validation errors (400)
      if (error?.code === 'INVALID_CREDENTIALS' ||
          error?.code === 'ACCOUNT_LOCKED' ||
          error?.code === 'AUTH_REQUIRED' ||
          error?.code === 'VALIDATION_ERROR') {
        throw error;
      }
      
      // Don't retry on last attempt
      if (attempt === maxRetries) {
        break;
      }
      
      // Calculate delay with exponential backoff
      const delay = baseDelay * Math.pow(2, attempt);
      console.warn(`Request failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms:`, error.message || error);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
}

// Login with email and password
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  return retryFetch(async () => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/auth/login`,
      {
        method: 'POST',
        credentials: 'include',
        headers: createAuthHeaders(), // Note: No token for login, but consistent header structure
        body: JSON.stringify(credentials),
      },
      15000 // 15 second timeout for login
    );

    if (response.status === 401) {
      const errorData = await response.json().catch(() => ({}));
      throw { 
        error: true, 
        message: errorData.message || 'Invalid email or password', 
        code: 'INVALID_CREDENTIALS' 
      } as AuthError;
    }

    if (response.status === 423) {
      const errorData = await response.json().catch(() => ({}));
      throw { 
        error: true, 
        message: errorData.message || 'Account is locked. Please try again later.', 
        code: 'ACCOUNT_LOCKED' 
      } as AuthError;
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // Check if it's a network/server error that should be retried
      if (response.status >= 500 || response.status === 0) {
        throw new Error(errorData.message || `Server error: ${response.statusText}`);
      }
      throw { 
        error: true, 
        message: errorData.message || `Login failed: ${response.statusText}`, 
        code: 'AUTH_REQUIRED' 
      } as AuthError;
    }

    const data = await response.json();
    
    // Store the JWT token if present in the response
    if (data.token) {
      localStorage.setItem('authToken', data.token);
      console.log('[Auth] JWT token stored in localStorage');
    } else {
      console.warn('[Auth] No token in login response');
    }
    
    return data;
  }, 3, 1000); // 3 retries, 1 second base delay
}

// Register new user with invitation token
export async function register(data: RegisterRequest): Promise<RegisterResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/register`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...data,
      termsAccepted: data.termsAccepted, // Ensure this is included
    }),
  });

  if (response.status === 400) {
    const errorData = await response.json().catch(() => ({}));
    
    // Handle terms not accepted error
    if (errorData.code === 'TERMS_NOT_ACCEPTED') {
      throw { 
        error: true, 
        message: errorData.message || 'You must accept the Terms and Conditions to register', 
        code: 'TERMS_NOT_ACCEPTED' 
      } as AuthError;
    }
    
    throw { 
      error: true, 
      message: errorData.message || 'Validation error', 
      code: 'VALIDATION_ERROR' 
    } as AuthError;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw { 
      error: true, 
      message: errorData.message || `Registration failed: ${response.statusText}`, 
      code: 'VALIDATION_ERROR' 
    } as AuthError;
  }

  return response.json();
}

// Request password reset
export async function forgotPassword(data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw { 
      error: true, 
      message: errorData.message || `Failed to send password reset email: ${response.statusText}`, 
      code: 'VALIDATION_ERROR' 
    } as AuthError;
  }

  const responseData = await response.json();
  // Log response for debugging email sending issues
  console.log('[ForgotPassword] Backend response:', {
    status: response.status,
    statusText: response.statusText,
    message: responseData.message,
    email: data.email
  });
  
  return responseData;
}

// Reset password with token
export async function resetPassword(data: ResetPasswordRequest): Promise<ResetPasswordResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (response.status === 400) {
    const errorData = await response.json().catch(() => ({}));
    throw { 
      error: true, 
      message: errorData.message || 'Invalid or expired token', 
      code: 'VALIDATION_ERROR' 
    } as AuthError;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw { 
      error: true, 
      message: errorData.message || `Password reset failed: ${response.statusText}`, 
      code: 'VALIDATION_ERROR' 
    } as AuthError;
  }

  return response.json();
}

// Update current user profile (display name)
export interface UpdateProfileRequest {
  displayName: string;
}

export async function updateProfile(data: UpdateProfileRequest): Promise<User> {
  const response = await fetch(`${API_BASE_URL}/auth/me`, {
    method: 'PATCH',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify({ displayName: data.displayName ?? '' }),
  });

  if (response.status === 400) {
    const errorData = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: errorData.message || 'Validation error',
      code: 'VALIDATION_ERROR',
    } as AuthError;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: errorData.message || 'Failed to update profile',
      code: 'AUTH_REQUIRED',
    } as AuthError;
  }

  return response.json();
}

// Change password (authenticated user)
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export async function changePassword(data: ChangePasswordRequest): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 400) {
    const errorData = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: errorData.message || 'Invalid current password or validation failed',
      code: 'VALIDATION_ERROR',
    } as AuthError;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: errorData.message || 'Failed to change password',
      code: 'AUTH_REQUIRED',
    } as AuthError;
  }

  return response.json();
}

// Get current user profile
export async function getCurrentUser(): Promise<User> {
  return retryFetch(async () => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/auth/me`,
      {
        method: 'GET',
        credentials: 'include',
        headers: createAuthHeaders(), // Include Authorization header with JWT token
      },
      10000 // 10 second timeout
    );

    if (response.status === 401) {
      throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as AuthError;
    }

    if (!response.ok) {
      // Check if it's a network/server error that should be retried
      if (response.status >= 500 || response.status === 0) {
        throw new Error(`Server error: ${response.statusText}`);
      }
      throw new Error(`Failed to get user: ${response.statusText}`);
    }

    return response.json();
  }, 3, 1000); // 3 retries, 1 second base delay
}

// Logout user
export async function logout(): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(), // Include Authorization header with JWT token
  });

  if (!response.ok) {
    throw new Error(`Failed to logout: ${response.statusText}`);
  }
  
  // Clear the token from localStorage on logout
  localStorage.removeItem('authToken');

  // Clear any local state
  window.location.href = '/';
}

